import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { departmentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import DateSelect from '../components/ui/DateSelect';
import PhoneInput from '../components/ui/PhoneInput';
import {
  isValidEmailLive,
  isValidCnp,
  isValidName,
  cnpBirthDateISO,
  getCnpBirthDate,
} from '../utils/validation';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Alert from '../components/ui/Alert';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import ConfirmModal from '../components/ui/ConfirmModal';
import Modal from '../components/ui/Modal';
import IntervalPicker from '../components/IntervalPicker';
import HelpTooltip from '../components/ui/HelpTooltip';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { canEditOwnIPField, canEditAnyIP } from '../utils/ipPermissions';
import { useToast } from '../context/ToastContext';

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    className="w-16 h-16 text-accent-600 mx-auto"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const KeyIcon = () => (
  <svg
    className="w-5 h-5 text-primary-500 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const SaveIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const EmployeeForm = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [searchParams] = useSearchParams();
  const preselectedCompanyId = searchParams.get('companyId');
  // Rolul poate veni din URL (ex: redirect din modalul „Adaugă administrator" → role=COMPANY_ADMIN
  // sau SSM_COMPANY_ADMIN, astfel încât adminul ajunge cu form-ul complet, prefixat pe rolul corect).
  const preselectedRole = searchParams.get('role');
  const { user } = useAuth();

  // În modul EDIT, datele de identitate (nume, prenume, data/locul nașterii) sunt blocate pentru
  // oricine altcineva decât SUPER_ADMIN. Schimbările oficiale de nume se fac prin butonul dedicat
  // „Schimbă numele de familie" de pe profilul angajatului, care emite o anexă legală la fișă.
  const lockedIdentity = isEditMode && user?.role !== 'SUPER_ADMIN';

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [companies, setCompanies] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [errors, setErrors] = useState({});
  // Câmpurile „atinse" de utilizator — eroarea unui câmp se afișează doar după ce a interacționat
  // cu el (la blur/selectare), ca să nu vadă erori înainte să apuce să completeze.
  const [touched, setTouched] = useState({});
  const markTouched = (name) => setTouched((t) => (t[name] ? t : { ...t, [name]: true }));
  const handleBlur = (e) => markTouched(e.target.name);
  const [saved, setSaved] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    jobTitle: '',
    departmentId: '',
    hireDate: '',
    employeeRole: preselectedRole || 'EMPLOYEE',
    companyId: preselectedCompanyId || '',
    isSSMInstructor: false,
    isCadruTehnicPSI: false,
    hasEPI: false,
    canManageEmployees: false,
    isCLM: false,
    clmDepartmentIds: [],
    // CNP — opțional la creare/edit (când HR-ul îl știe). Dacă nu e completat, angajatul îl
    // introduce singur la primul login prin wizard. La edit, dacă există deja CNP criptat,
    // nu îl pre-populăm (nu-l decriptăm pe formular) — câmpul rămâne gol și ascuns.
    cnp: '',
    // Intervale IP
    ipSSMIntervalDays: '',
    ipSUIntervalDays: '',
    // Date personale extra
    homeAddress: '',
    birthDate: '',
    birthPlace: '',
    badgeNumber: '',
    bloodGroup: '',
    qualification: '',
    specialAuthorizations: '',
  });

  // Snapshot starea CLM încărcată inițial (în edit mode) — utilizat pentru a detecta
  // schimbările care necesită confirmare (debifare isCLM, înlocuire manager existent).
  const [initialCLMState, setInitialCLMState] = useState({ isCLM: false, deptIds: [] });
  const [hasCNPSaved, setHasCNPSaved] = useState(false);

  // Snapshot funcția (jobTitle) încărcată inițial în edit mode — folosit pentru a detecta
  // schimbarea funcției și a afișa warning-ul „necesită ILM nou" după salvare.
  const [initialJobTitle, setInitialJobTitle] = useState('');
  // Warning informativ după salvare când funcția s-a schimbat (nu blochează nimic).
  const [jobTitleWarning, setJobTitleWarning] = useState(false);
  // Flag pentru pre-completarea jobTitle din rol: true dacă valoarea curentă a fost setată
  // automat la selectarea unui rol, false dacă userul a tastat ceva. Folosit ca să decidem dacă
  // schimbarea ulterioară a rolului trebuie să actualizeze jobTitle (era auto → da) sau să-l
  // lase neatins (era manual → nu-l ștergem accidental).
  const [jobTitleAutoFilled, setJobTitleAutoFilled] = useState(false);

  // Confirmare dialog pentru schimbări sensibile de CLM (înlocuire / debifare).
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm }

  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN') {
      fetchCompanies();
    } else if (user?.role === 'SSM_COMPANY_ADMIN' || user?.role === 'SSM_INSTRUCTOR') {
      fetchCompanies();
    } else {
      // COMPANY_ADMIN — folosește propria firmă
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (!preselectedCompanyId) {
        setFormData((prev) => ({ ...prev, companyId: stored.companyId || user?.companyId || '' }));
      }
    }
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (e) {}
  };

  // Fetch employee data in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    const fetchEmployee = async () => {
      try {
        const res = await api.get(`/employees/${id}`);
        const emp = res.data;
        const initialDeptIds = (emp.managedDepartments || []).map((d) => d.id);
        setFormData({
          email: emp.user?.email || '',
          firstName: emp.user?.firstName || '',
          lastName: emp.user?.lastName || '',
          phone: emp.user?.phone || '',
          jobTitle: emp.jobTitle || '',
          departmentId: emp.departmentId || '',
          hireDate: emp.hireDate ? emp.hireDate.split('T')[0] : '',
          employeeRole: emp.user?.role || 'EMPLOYEE',
          companyId: emp.companyId || '',
          isSSMInstructor: emp.user?.isSSMInstructor || false,
          isCadruTehnicPSI: emp.user?.isCadruTehnicPSI || false,
          canManageEmployees: emp.user?.canManageEmployees || false,
          isCLM: emp.isCLM || false,
          clmDepartmentIds: initialDeptIds,
          cnp: '', // niciodată nu pre-populăm CNP-ul (criptat la DB)
          hasEPI: emp.hasEPI || false,
          ipSSMIntervalDays: emp.ipSSMIntervalDays ?? '',
          ipSUIntervalDays: emp.ipSUIntervalDays ?? '',
          homeAddress: emp.homeAddress || '',
          birthDate: emp.birthDate ? emp.birthDate.split('T')[0] : '',
          birthPlace: emp.birthPlace || '',
          badgeNumber: emp.badgeNumber || '',
          bloodGroup: emp.bloodGroup || '',
          qualification: emp.qualification || '',
          specialAuthorizations: emp.specialAuthorizations || '',
        });
        setInitialCLMState({ isCLM: !!emp.isCLM, deptIds: initialDeptIds });
        setInitialJobTitle(emp.jobTitle || '');
        setHasCNPSaved(!!emp.hasCNP);
      } catch (err) {
        setErrors({ general: err.response?.data?.error || t('toasts.errorLoad') });
      } finally {
        setLoadingData(false);
      }
    };
    fetchEmployee();
  }, [id, isEditMode]);

  // Fetch departments when companyId changes + când utilizatorul revine pe tab.
  // Anterior, lista era încărcată o singură dată la mount → dacă userul ștergea/crea
  // departamente într-un alt tab, dropdown-ul rămânea stale și salvarea eșua cu
  // „departamentul nu aparține firmei". Refresh pe focus rezolvă cazul comun.
  useEffect(() => {
    const cId = formData.companyId;
    if (!cId) {
      setDepartmentsList([]);
      return;
    }

    const fetchDepts = () => {
      departmentsAPI
        .getAll(cId)
        .then((res) => setDepartmentsList(res.data))
        .catch(() => setDepartmentsList([]));
    };
    fetchDepts();

    // Re-fetch când userul revine pe tab (visibilitychange) sau dă focus pe fereastră
    // (ex: după ce a șters un departament într-un alt tab și se întoarce).
    const onFocus = () => fetchDepts();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchDepts();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [formData.companyId]);

  // Marcaj intern: IP SU a fost setat automat din regula EPI?
  const [ipSUFromEPI, setIpSUFromEPI] = useState(false);

  // Servicii oferite de firma selectată — determină ce flag-uri/opțiuni apar.
  const selectedCompanyObj = companies.find((c) => c.id === formData.companyId);
  const companyOffersSSM = selectedCompanyObj
    ? selectedCompanyObj.offersSSM !== false
    : user?.offersSSM !== false;
  const companyOffersSU = selectedCompanyObj
    ? selectedCompanyObj.offersSU !== false
    : user?.offersSU !== false;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // SSM_INSTRUCTOR este deja instructor implicit — eliminăm flag-ul redundant
      // Rolul SSM_INSTRUCTOR implică deja SSM — flag-ul e redundant doar pentru acest rol
      if (name === 'employeeRole' && value === 'SSM_INSTRUCTOR') next.isSSMInstructor = false;
      // EPI bifat → default IP SU 30 zile dacă nu e setat (doar dacă firma oferă SU)
      if (name === 'hasEPI' && checked && !prev.ipSUIntervalDays && companyOffersSU) {
        next.ipSUIntervalDays = 30;
        setIpSUFromEPI(true);
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // CNP: la schimbare, dacă e valid și data nașterii e goală, o pre-completăm din CNP.
  const handleCnpChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, cnp: value };
      if (!prev.birthDate && isValidCnp(value)) {
        const iso = cnpBirthDateISO(value);
        if (iso) next.birthDate = iso;
      }
      return next;
    });
    if (errors.cnp) setErrors((prev) => ({ ...prev, cnp: '' }));
    if (errors.birthDate) setErrors((prev) => ({ ...prev, birthDate: '' }));
  };

  const setIPSSM = (val) => setFormData((prev) => ({ ...prev, ipSSMIntervalDays: val ?? '' }));
  const setIPSU = (val) => {
    setFormData((prev) => ({ ...prev, ipSUIntervalDays: val ?? '' }));
    setIpSUFromEPI(false); // utilizatorul a modificat manual → eticheta dispare
  };

  // Toggle un departament în lista clmDepartmentIds (multi-select cu checkbox-uri)
  const toggleCLMDepartment = (deptId) => {
    setFormData((prev) => {
      const has = prev.clmDepartmentIds.includes(deptId);
      return {
        ...prev,
        clmDepartmentIds: has
          ? prev.clmDepartmentIds.filter((id) => id !== deptId)
          : [...prev.clmDepartmentIds, deptId],
      };
    });
    if (errors.clmDepartmentIds) setErrors((prev) => ({ ...prev, clmDepartmentIds: '' }));
  };

  const companyHasDepartments = departmentsList.length > 0;

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = t('employees.valFirstNameRequired');
    else if (!isValidName(formData.firstName)) e.firstName = t('employees.valNameInvalid');
    if (!formData.lastName.trim()) e.lastName = t('employees.valLastNameRequired');
    else if (!isValidName(formData.lastName)) e.lastName = t('employees.valNameInvalid');
    if (!formData.email.trim()) e.email = t('employees.valEmailRequired');
    else if (!isValidEmailLive(formData.email)) e.email = t('employees.valEmailInvalid');
    if (!formData.birthDate) e.birthDate = t('employees.valBirthDateRequired');
    else {
      // Data nașterii: nu în viitor, vârstă 16-100.
      const bd = new Date(formData.birthDate);
      const today = new Date();
      if (bd > today) e.birthDate = t('employees.valBirthFuture');
      else {
        const age = (today - bd) / (365.25 * 24 * 3600 * 1000);
        if (age < 16 || age > 100) e.birthDate = t('employees.valAgeRange');
      }
    }
    if (!formData.birthPlace.trim()) e.birthPlace = t('employees.valBirthPlaceRequired');
    // Telefon OPȚIONAL — validăm formatul doar dacă e completat. Validare reală prin libphonenumber
    // (RO ca țară implicită); valoarea vine deja în format E.164 din PhoneInput.
    if (formData.phone.trim()) {
      let phoneOk = false;
      try {
        phoneOk = isValidPhoneNumber(formData.phone.trim(), 'RO');
      } catch (_) {
        phoneOk = false;
      }
      if (!phoneOk) e.phone = t('employees.valPhoneInvalid');
    }
    if (!formData.hireDate) e.hireDate = t('employees.valHireDateRequired');
    else if (formData.birthDate && new Date(formData.hireDate) < new Date(formData.birthDate))
      e.hireDate = t('employees.valHireBeforeBirth');
    if (!formData.jobTitle.trim()) e.jobTitle = t('employees.valJobTitleRequired');
    if (!formData.companyId) e.companyId = t('employees.valCompanyRequired');
    if (!formData.employeeRole) e.employeeRole = t('employees.valRoleRequired');
    // CLM: dacă bifa e activă și firma are departamente, e obligatoriu să selecteze ≥1.
    if (formData.isCLM && companyHasDepartments && formData.clmDepartmentIds.length === 0) {
      e.clmDepartmentIds = t('employees.valCLMDeptRequired');
    }
    // CNP — dacă e completat: cifra de control validă + potrivire cu data nașterii.
    // Sărit pentru firme demo (CNP poate fi fictiv — testare semnătură fără date reale).
    if (formData.cnp && !selectedCompanyObj?.isDemo) {
      if (!isValidCnp(formData.cnp)) {
        e.cnp = t('employees.valCNPInvalid');
      } else if (formData.birthDate) {
        const cnpD = getCnpBirthDate(formData.cnp);
        const bd = new Date(formData.birthDate);
        if (
          cnpD &&
          !isNaN(bd.getTime()) &&
          (cnpD.getUTCFullYear() !== bd.getUTCFullYear() ||
            cnpD.getUTCMonth() !== bd.getUTCMonth() ||
            cnpD.getUTCDate() !== bd.getUTCDate())
        ) {
          e.cnp = t('employees.valCnpMismatch');
        }
      }
    }
    return e;
  };

  // Validare LIVE: recalculează erorile la fiecare schimbare, dar afișează doar pentru câmpurile
  // deja atinse (touched). Astfel feedback-ul apare imediat ce corectezi/completezi, fără să fie
  // nevoie să apeși Salvează și să te întorci.
  useEffect(() => {
    const all = validate();
    setErrors((_prev) => {
      const shown = {};
      for (const k in all) if (touched[k]) shown[k] = all[k];
      return shown;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, touched]);

  // Detectează scenarii care necesită confirmare înainte de salvare:
  //  • Înlocuire manager existent pe departamente alese — confirm dialog cu numele înlocuiților.
  //  • Debifare isCLM (în edit mode) când userul curent era manager pe ≥1 departament — confirm că rămân fără.
  // Returnează mesajul + titlul dialogului, sau null dacă nu e necesară confirmare.
  const checkCLMConfirmations = () => {
    // 1) Debifare isCLM cu manageri activi
    if (
      isEditMode &&
      initialCLMState.isCLM &&
      !formData.isCLM &&
      initialCLMState.deptIds.length > 0
    ) {
      const names = departmentsList
        .filter((d) => initialCLMState.deptIds.includes(d.id))
        .map((d) => d.name)
        .join(', ');
      return {
        title: t('employees.clmUncheckTitle'),
        message: t('employees.clmUncheckMsg', {
          name: `${formData.firstName} ${formData.lastName}`,
          depts: names || t('employees.clmDeptsCount', { count: initialCLMState.deptIds.length }),
        }),
      };
    }

    // 2) Înlocuire manager existent (doar pe departamentele unde el nu era deja manager)
    if (formData.isCLM && formData.clmDepartmentIds.length > 0) {
      const conflicts = [];
      for (const did of formData.clmDepartmentIds) {
        if (initialCLMState.deptIds.includes(did)) continue; // era deja manager — nu e conflict
        const d = departmentsList.find((x) => x.id === did);
        if (!d || !d.manager) continue;
        conflicts.push(
          t('employees.clmReplaceConflict', {
            dept: d.name,
            manager: `${d.manager.firstName} ${d.manager.lastName}`,
          })
        );
      }
      if (conflicts.length > 0) {
        return {
          title: t('employees.clmReplaceTitle'),
          message: t('employees.clmReplaceMsg', {
            name: `${formData.firstName} ${formData.lastName}`,
            conflicts: conflicts.join('; '),
          }),
        };
      }
    }

    return null;
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      // Payload-ul de salvare — la edit, scoatem email-ul (readonly), trimitem isCLM + clmDepartmentIds + cnp.
      if (isEditMode) {
        const { email: _email, ...editData } = formData;
        await api.put(`/employees/${id}`, editData);
      } else {
        await api.post('/employees', formData);
      }
      // Warning ILM: dacă în edit mode funcția (jobTitle) s-a schimbat efectiv, afișăm un
      // pop-up informativ (nu blochează) și amânăm redirectul până când userul îl închide.
      // Nu setăm `saved` în acest caz, ca să rămână montat formularul peste care apare modalul
      // (ecranul de succes are return propriu mai sus și ar ascunde warning-ul).
      const jobTitleChanged = isEditMode && initialJobTitle.trim() !== formData.jobTitle.trim();
      if (jobTitleChanged) {
        setJobTitleWarning(true);
        return;
      }
      setSaved(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
      document.querySelector('[class*="overflow"]')?.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(
        () => navigate(preselectedCompanyId ? `/companies/${preselectedCompanyId}` : '/employees'),
        1200
      );
    } catch (err) {
      const msg = err.response?.data?.error || t('employees.saveError');
      if (msg.toLowerCase().includes('email')) setErrors({ email: msg });
      else showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setTouched((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.keys(ve).map((k) => [k, true])),
      }));
      setErrors(ve);
      document
        .querySelector('.field-error')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const need = checkCLMConfirmations();
    if (need) {
      setConfirmDialog({
        title: need.title,
        message: need.message,
        onConfirm: async () => {
          setConfirmDialog(null);
          await submitForm();
        },
      });
      return;
    }
    await submitForm();
  };

  const availableRoles = () => {
    if (!user) return [];
    const lbl = {
      SSM_COMPANY_ADMIN: t('employees.roleAdminSSM'),
      SSM_INSTRUCTOR: t('employees.roleInstructorSSM'),
      COMPANY_ADMIN: t('employees.roleCompanyAdmin'),
      DIRECTOR_GENERAL: t('employees.roleDirectorGeneral'),
      EMPLOYEE: t('employees.roleEmployee'),
    };
    if (user.role === 'SUPER_ADMIN')
      return [
        { value: 'SSM_COMPANY_ADMIN', label: lbl.SSM_COMPANY_ADMIN },
        { value: 'SSM_INSTRUCTOR', label: lbl.SSM_INSTRUCTOR },
        { value: 'COMPANY_ADMIN', label: lbl.COMPANY_ADMIN },
        { value: 'DIRECTOR_GENERAL', label: lbl.DIRECTOR_GENERAL },
        { value: 'EMPLOYEE', label: lbl.EMPLOYEE },
      ];
    if (user.role === 'SSM_COMPANY_ADMIN' || user.role === 'SSM_INSTRUCTOR')
      return [
        { value: 'SSM_INSTRUCTOR', label: lbl.SSM_INSTRUCTOR },
        { value: 'COMPANY_ADMIN', label: lbl.COMPANY_ADMIN },
        { value: 'DIRECTOR_GENERAL', label: lbl.DIRECTOR_GENERAL },
        { value: 'EMPLOYEE', label: lbl.EMPLOYEE },
      ];
    // HR (EMPLOYEE cu canManageEmployees — flag individual SAU departament isHR) e aliniat cu
    // COMPANY_ADMIN pe people-management: poate alege Director/Angajat (NU poate crea alt admin firmă).
    if (user.role === 'COMPANY_ADMIN' || (user.role === 'EMPLOYEE' && user.canManageEmployees))
      return [
        { value: 'DIRECTOR_GENERAL', label: lbl.DIRECTOR_GENERAL },
        { value: 'EMPLOYEE', label: lbl.EMPLOYEE },
      ];
    return [{ value: 'EMPLOYEE', label: lbl.EMPLOYEE }];
  };

  const backPath = isEditMode
    ? `/employees/${id}`
    : preselectedCompanyId
      ? `/companies/${preselectedCompanyId}`
      : '/employees';

  if (loadingData) return <Spinner size="lg" label={t('employees.loadingData')} />;

  if (saved)
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="py-12 text-center">
          <CheckCircleIcon />
          <h2 className="mt-4 text-2xl font-bold text-accent-800 dark:text-accent-200">
            {isEditMode ? t('employees.updatedSuccess') : t('employees.createdSuccess')}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {t('employees.redirecting')}
          </p>
        </Card>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title={isEditMode ? t('employees.editEmployee') : t('employees.newEmployee')}
        subtitle=""
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
            <ArrowLeftIcon />
            {t('common.back')}
          </Button>
        }
      />

      <Card padding={false} className="mt-6">
        <form
          onSubmit={handleSubmit}
          className="divide-y divide-slate-100 dark:divide-slate-800"
          noValidate
        >
          {/* Company field — read-only dacă vine din URL, dropdown altfel */}
          {['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role) && (
            <div className="p-6">
              {preselectedCompanyId ? (
                <Input
                  label={t('nav.companies')}
                  value={
                    companies.find((c) => c.id === preselectedCompanyId)?.name ||
                    t('common.loading')
                  }
                  readOnly
                  disabled
                />
              ) : (
                companies.length > 0 && (
                  <Select
                    label={t('nav.companies') + ' *'}
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleChange}
                    placeholder="—"
                    options={companies.map((c) => ({ value: c.id, label: `${c.name} (${c.cui})` }))}
                    error={errors.companyId}
                  />
                )
              )}
            </div>
          )}

          {/* Section 1: Personal data */}
          <div className="p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-7 h-7 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                1
              </span>
              <h2 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('employees.sectionPersonal')}
              </h2>
            </div>

            {lockedIdentity && (
              <div className="mb-4 p-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-xs text-amber-800 dark:text-amber-300">
                {t('lockedIdentity.banner')}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('employees.firstName') + ' *'}
                name="firstName"
                value={formData.firstName}
                onChange={lockedIdentity ? undefined : handleChange}
                onBlur={handleBlur}
                autoComplete="given-name"
                error={errors.firstName}
                readOnly={lockedIdentity}
                disabled={lockedIdentity}
              />
              <Input
                label={t('employees.lastName') + ' *'}
                name="lastName"
                value={formData.lastName}
                onChange={lockedIdentity ? undefined : handleChange}
                onBlur={handleBlur}
                autoComplete="family-name"
                error={errors.lastName}
                readOnly={lockedIdentity}
                disabled={lockedIdentity}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Data nașterii — selector unitar Zi/Lună/An (DateSelect), același peste tot. */}
              <div>
                <label className="input-label">{t('employees.birthDate')} *</label>
                <DateSelect
                  value={formData.birthDate}
                  onChange={(v) => {
                    setFormData((prev) => ({ ...prev, birthDate: v }));
                    markTouched('birthDate');
                  }}
                  disabled={lockedIdentity}
                  yearStart={1940}
                  error={errors.birthDate}
                  birthDate
                />
              </div>
              <Input
                label={t('employees.birthPlace') + ' *'}
                name="birthPlace"
                value={formData.birthPlace}
                onChange={lockedIdentity ? undefined : handleChange}
                onBlur={handleBlur}
                error={errors.birthPlace}
                readOnly={lockedIdentity}
                disabled={lockedIdentity}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <PhoneInput
                label={t('common.phone')}
                value={formData.phone}
                onChange={(v) => {
                  setFormData((prev) => ({ ...prev, phone: v }));
                }}
                onBlur={() => markTouched('phone')}
                error={errors.phone}
              />
              <div>
                <label className="input-label">{t('employees.hireDate')} *</label>
                <DateSelect
                  value={formData.hireDate}
                  onChange={(v) => {
                    setFormData((prev) => ({ ...prev, hireDate: v }));
                    markTouched('hireDate');
                  }}
                  yearStart={1980}
                  error={errors.hireDate}
                  maxDate={null}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label={t('employees.badge')}
                name="badgeNumber"
                value={formData.badgeNumber}
                onChange={handleChange}
              />
              <Input
                label={t('employees.bloodGroup')}
                name="bloodGroup"
                value={formData.bloodGroup}
                onChange={handleChange}
              />
            </div>

            {/* CNP — opțional la creare/edit. Dacă există deja CNP salvat, afișăm un placeholder
                explicativ (CNP-ul rămâne criptat — nu îl decriptăm pe formular). HR-ul îl poate
                introduce dacă nu a fost completat de angajat. Format obligatoriu: 13 cifre. */}
            <div className="mt-4">
              {/* #4: CNP setat → editabil DOAR de roluri autorizate (super admin / company admin /
                  ssm company admin / HR). Pentru ceilalți (ex: CLM) rămâne read-only.
                  CNP nesetat → editabil de oricine gestionează angajații (au ajuns pe acest formular). */}
              {hasCNPSaved &&
              !(
                ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'COMPANY_ADMIN'].includes(user?.role) ||
                user?.canManageEmployees
              ) ? (
                <div className="p-3 rounded-md border border-accent-200 dark:border-accent-800 bg-accent-50 dark:bg-accent-950/30 text-xs text-accent-800 dark:text-accent-300 flex items-start gap-2">
                  <svg
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('employees.cnpSavedNote')}</span>
                </div>
              ) : (
                <Input
                  label={t('employees.cnpOptional')}
                  name="cnp"
                  value={formData.cnp}
                  onChange={handleCnpChange}
                  onBlur={handleBlur}
                  placeholder={hasCNPSaved ? '•••••••••••••' : t('employees.cnpPlaceholder')}
                  error={errors.cnp}
                  helper={
                    hasCNPSaved
                      ? t('employees.cnpSavedNote')
                      : `${t('employees.cnpHelper')} ${t('employees.cnpAutofillNote')}`
                  }
                  maxLength={13}
                />
              )}
            </div>
          </div>

          {/* Section 2: Account */}
          <div className="p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-7 h-7 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                2
              </span>
              <h2 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('employees.sectionAccount')}
              </h2>
            </div>

            <Input
              label={t('common.email') + ' *'}
              type="email"
              name="email"
              value={formData.email}
              onChange={isEditMode ? undefined : handleChange}
              onBlur={isEditMode ? undefined : handleBlur}
              autoComplete="email"
              error={errors.email}
              readOnly={isEditMode}
              disabled={isEditMode}
            />

            {!isEditMode && (
              <Alert type="info" className="mt-4">
                <div className="flex items-center gap-3">
                  <KeyIcon />
                  <div>
                    <p className="text-sm font-semibold text-primary-800 dark:text-primary-200">
                      {t('employeeForm.tempPasswordTitle')}
                    </p>
                    <p className="text-xs text-primary-600 dark:text-primary-300 mt-0.5">
                      {t('employees.defaultPasswordNote')}
                    </p>
                  </div>
                </div>
              </Alert>
            )}
          </div>

          {/* Section 3: Job and role */}
          <div className="p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-7 h-7 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                3
              </span>
              <h2 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                {t('employees.sectionJobRole')}
              </h2>
            </div>

            {/* Rolul în aplicație e MUTAT ÎNAINTE de Funcție/Job title — anterior userii scriau
                „Administrator firmă" la funcție dar uitau să bifeze rolul corespunzător (rămânea
                default EMPLOYEE), pierzând puterile contului în app. Acum rolul e prima decizie;
                schimbarea lui prefill-uiește jobTitle dacă e încă gol (nu suprascrie text deja tastat). */}
            <div>
              <label className="input-label">{t('employees.rolInApp')} *</label>
              <div className="space-y-2 mt-1">
                {availableRoles().map((role) => (
                  <label
                    key={role.value}
                    className={`flex items-center gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                      formData.employeeRole === role.value
                        ? 'border-primary-500 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50'
                        : 'border-slate-200 dark:border-slate-800 hover:border-primary-200 hover:bg-surface-secondary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="employeeRole"
                      value={role.value}
                      checked={formData.employeeRole === role.value}
                      onChange={(e) => {
                        // Prefill jobTitle din label-ul rolului în 2 cazuri:
                        //  (a) jobTitle e gol — pre-completare inițială
                        //  (b) jobTitle a fost auto-completat anterior din alt rol — actualizăm
                        //      cu noul rol. Dacă userul a tastat manual, NU-l atingem.
                        setFormData((prev) => {
                          const next = { ...prev, employeeRole: e.target.value };
                          const shouldPrefill =
                            (!prev.jobTitle?.trim() || jobTitleAutoFilled) && role.label;
                          if (shouldPrefill) next.jobTitle = role.label;
                          if (e.target.value === 'SSM_INSTRUCTOR') next.isSSMInstructor = false;
                          return next;
                        });
                        if (role.label) setJobTitleAutoFilled(true);
                        if (errors.employeeRole)
                          setErrors((prev) => ({ ...prev, employeeRole: '' }));
                        if (errors.jobTitle) setErrors((prev) => ({ ...prev, jobTitle: '' }));
                      }}
                      className="h-4 w-4 text-primary-600 flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {role.label}
                    </span>
                    {formData.employeeRole === role.value && (
                      <span className="ml-auto text-primary-500 dark:text-primary-300">
                        <CheckIcon />
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {errors.employeeRole && (
                <p className="input-error-text field-error" role="alert">
                  {errors.employeeRole}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label={t('employees.jobTitle') + ' *'}
                name="jobTitle"
                value={formData.jobTitle}
                onChange={(e) => {
                  handleChange(e);
                  // Userul a tastat manual → nu mai considerăm câmpul „auto-completat".
                  // Schimbarea ulterioară a rolului nu va mai suprascrie valoarea tastată.
                  if (jobTitleAutoFilled) setJobTitleAutoFilled(false);
                }}
                onBlur={handleBlur}
                error={errors.jobTitle}
                helper={t('employees.jobTitleHelper')}
              />
              <Select
                label={t('employees.department')}
                name="departmentId"
                value={formData.departmentId}
                onChange={handleChange}
                placeholder="—"
                options={departmentsList.map((d) => ({ value: d.id, label: d.name }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label={t('employees.qualification')}
                name="qualification"
                value={formData.qualification}
                onChange={handleChange}
              />
              <Input
                label={t('employees.specialAuths')}
                name="specialAuthorizations"
                value={formData.specialAuthorizations}
                onChange={handleChange}
              />
            </div>

            {/* CLM — Conducător Loc de Muncă. Secțiune dedicată, vizibilă imediat sub roluri pentru
                că e ortogonală (oricine de orice rol poate fi CLM). Înainte era îngropată în
                detaliile „Competențe" și user-ul nu o vedea ușor. */}
            <div className="mt-4">
              <div
                className={`p-4 rounded-xl border-2 transition ${formData.isCLM ? 'border-primary-500 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/40' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900'}`}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isCLM"
                    checked={formData.isCLM}
                    onChange={handleChange}
                    className="h-5 w-5 mt-0.5 text-primary-600 rounded flex-shrink-0"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                      {t('employees.clmTitle')}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                      {t('employees.clmDesc')}
                      {!companyHasDepartments && ' ' + t('employees.clmNoDeptsNote')}
                    </p>
                  </div>
                </label>

                {formData.isCLM && companyHasDepartments && (
                  <div className="mt-3 ml-8 p-3 rounded-lg border border-primary-300 dark:border-primary-700 bg-white dark:bg-slate-800">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
                      {t('employees.clmDeptsLabel')} <span className="text-danger-600">*</span>
                    </p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {departmentsList.map((d) => {
                        const checked = formData.clmDepartmentIds.includes(d.id);
                        const managerNote = d.manager
                          ? initialCLMState.deptIds.includes(d.id)
                            ? t('employees.clmDeptCurrentUser')
                            : t('employees.clmDeptReplaced', {
                                name: `${d.manager.firstName} ${d.manager.lastName}`,
                              })
                          : t('employees.clmDeptNoManager');
                        return (
                          <label
                            key={d.id}
                            className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleCLMDepartment(d.id)}
                              className="h-4 w-4 text-primary-600 rounded"
                            />
                            <span className="text-sm text-slate-700 dark:text-slate-200">
                              {d.name}
                            </span>
                            <span
                              className={`text-[11px] ${d.manager && !initialCLMState.deptIds.includes(d.id) ? 'text-amber-700 dark:text-amber-300' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                              {managerNote}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {errors.clmDepartmentIds && (
                      <p className="input-error-text field-error mt-2" role="alert">
                        {errors.clmDepartmentIds}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {formData.employeeRole &&
              (() => {
                // Doar rolul SSM_INSTRUCTOR implică SSM — pentru SSM_COMPANY_ADMIN flag-ul rămâne relevant
                const showSSMInstructorFlag = formData.employeeRole !== 'SSM_INSTRUCTOR';
                const activeFlagsCount = [
                  showSSMInstructorFlag && formData.isSSMInstructor,
                  formData.isCadruTehnicPSI,
                  formData.hasEPI,
                  formData.canManageEmployees,
                ].filter(Boolean).length;
                return (
                  <details className="mt-4 group">
                    <summary className="flex items-center justify-between cursor-pointer p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition select-none">
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform group-open:rotate-90"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                          {t('employees.competences')}
                        </span>
                        {activeFlagsCount > 0 && (
                          <span className="ml-1 text-[10px] font-bold bg-primary-600 text-white rounded-full px-2 py-0.5">
                            {activeFlagsCount}
                          </span>
                        )}
                        <span onClick={(e) => e.stopPropagation()}>
                          <HelpTooltip
                            section="add-employee"
                            text={t('employees.competencesHelp')}
                          />
                        </span>
                      </div>
                      <span className="text-xs text-slate-400 dark:text-slate-400">
                        {t('employees.optional')}
                      </span>
                    </summary>

                    <div className="mt-3 space-y-4">
                      {/* Grupul 1: Competențe SSM/SU */}
                      {/* Competențe SSM/SU — afișate doar dacă firma oferă serviciul corespunzător */}
                      {(companyOffersSSM || companyOffersSU) && (
                        <div>
                          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                            {t('employees.competencesSSMSU')}
                          </p>
                          <div className="space-y-2">
                            {showSSMInstructorFlag && companyOffersSSM && (
                              <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                                <input
                                  type="checkbox"
                                  name="isSSMInstructor"
                                  checked={formData.isSSMInstructor}
                                  onChange={handleChange}
                                  className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                    {t('employees.instructorSSMInternal')}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('employees.instructorSSMHelp')}
                                  </p>
                                </div>
                              </label>
                            )}
                            {companyOffersSU && (
                              <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                                <input
                                  type="checkbox"
                                  name="isCadruTehnicPSI"
                                  checked={formData.isCadruTehnicPSI}
                                  onChange={handleChange}
                                  className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                    {t('employees.cadruPSI')}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('employees.cadruPSIHelp')}
                                  </p>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Grupul 2: Roluri operaționale */}
                      <div>
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                          {t('common.actions')}
                        </p>
                        <div className="space-y-2">
                          {/* EPI apare doar dacă firma oferă SU (EPI = concept PSI) */}
                          {companyOffersSU && (
                            <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                              <input
                                type="checkbox"
                                name="hasEPI"
                                checked={formData.hasEPI}
                                onChange={handleChange}
                                className="h-4 w-4 mt-0.5 text-danger-600 rounded"
                              />
                              <div>
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                  {t('employees.beneficiarEPI')}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {t('employees.beneficiarEPIHelp')}
                                </p>
                              </div>
                            </label>
                          )}
                          <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                            <input
                              type="checkbox"
                              name="canManageEmployees"
                              checked={formData.canManageEmployees}
                              onChange={handleChange}
                              className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                {t('employees.canManageEmployees')}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {t('employees.canManageEmployeesHelp')}
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </details>
                );
              })()}
          </div>

          {/* Section 4: Intervale IP — vizibil dacă poate edita cel puțin un câmp */}
          {canEditAnyIP(user) && (companyOffersSSM || companyOffersSU) && (
            <div className="p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <span className="w-7 h-7 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  4
                </span>
                <h2 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  {/* Titlul afișează doar serviciile pe care firma le oferă, ca să fie clar din start */}
                  {companyOffersSSM && companyOffersSU
                    ? `${t('employees.ipSSM')} / ${t('employees.ipSU')}`
                    : companyOffersSSM
                      ? t('employees.ipSSM')
                      : t('employees.ipSU')}
                </h2>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-5">
                {t('employees.ipSectionHint')}
              </p>
              <div
                className={`grid grid-cols-1 ${companyOffersSSM && companyOffersSU ? 'md:grid-cols-2' : ''} gap-8`}
              >
                {/* IP SSM — vizibil doar dacă firma oferă serviciul SSM */}
                {companyOffersSSM &&
                  (canEditOwnIPField(user, 'ssm') ? (
                    <IntervalPicker
                      label="IP SSM"
                      value={formData.ipSSMIntervalDays || null}
                      onChange={setIPSSM}
                    />
                  ) : (
                    <div className="opacity-60">
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
                        IP SSM
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                        {t('employees.ipSSMReadOnly')}
                      </p>
                    </div>
                  ))}
                {/* IP SU — vizibil doar dacă firma oferă serviciul SU */}
                {companyOffersSU &&
                  (canEditOwnIPField(user, 'su') ? (
                    <IntervalPicker
                      label="IP SU"
                      value={formData.ipSUIntervalDays || null}
                      onChange={setIPSU}
                      hint={
                        ipSUFromEPI && formData.ipSUIntervalDays === 30 ? (
                          <div className="inline-flex items-start gap-2 text-xs bg-amber-50 dark:bg-slate-800/60 border border-amber-200 dark:border-slate-700 text-amber-800 dark:text-amber-300 rounded-md px-2.5 py-1.5">
                            <span>🛡️</span>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(t('employees.ipSUAutoEPI')),
                              }}
                            />
                          </div>
                        ) : null
                      }
                    />
                  ) : (
                    <div className="opacity-60">
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
                        IP SU
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                        {t('employees.ipSUReadOnly')}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Error summary */}
          {Object.keys(errors).length > 0 && (
            <div className="px-6 pb-0 pt-0">
              <Alert type="error">
                <p className="text-sm font-semibold">{t('employees.errorSummary')}</p>
              </Alert>
            </div>
          )}

          {/* Actions */}
          <div className="p-6 flex flex-col sm:flex-row gap-3">
            <Button type="submit" variant="primary" size="lg" loading={loading} className="flex-1">
              {!loading && <SaveIcon />}
              {loading ? t('common.loading') : t('common.save')}
            </Button>
            <Button type="button" variant="secondary" size="lg" onClick={() => navigate(backPath)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Warning informativ după salvare: funcția (jobTitle) s-a schimbat → necesită ILM nou.
          Nu blochează nimic; la închidere continuă redirectul normal. */}
      <Modal
        open={jobTitleWarning}
        onClose={() => {
          setJobTitleWarning(false);
          navigate(preselectedCompanyId ? `/companies/${preselectedCompanyId}` : '/employees');
        }}
        title={t('employees.jobTitleChangedTitle')}
        size="md"
        footer={
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              setJobTitleWarning(false);
              navigate(preselectedCompanyId ? `/companies/${preselectedCompanyId}` : '/employees');
            }}
          >
            {t('employees.jobTitleChangedOk')}
          </Button>
        }
      >
        <div className="flex items-start gap-3">
          <svg
            className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {t('employees.jobTitleChangedMsg')}
          </p>
        </div>
      </Modal>

      {/* Confirmare pentru schimbări CLM cu impact (înlocuire manager / debifare) */}
      <ConfirmModal
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onConfirm={() => confirmDialog?.onConfirm?.()}
        title={confirmDialog?.title || t('employees.confirmTitle')}
        message={confirmDialog?.message || ''}
        confirmLabel={t('employees.confirmContinue')}
        cancelLabel={t('common.cancel')}
        variant="primary"
        loading={loading}
      />
    </div>
  );
};

export default EmployeeForm;
